import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { FileText, Video } from 'lucide-react';
import { ChoiceCard, DialogShellLayout, DialogShellSection, Label } from '@bliks-io/ui';
import {
  PARTNER_MATERIAL_CATEGORIES,
  PARTNER_MATERIAL_LOCALES,
  parseYouTubeVideoId,
  type PartnerMaterialAdmin,
  type PartnerMaterialLocale
} from '@bliks-io/contracts';
import { FormInput, FormSelect, FormTextarea } from '@/components/form/rhf';
import {
  PARTNER_MATERIAL_CATEGORY_KEYS,
  PARTNER_MATERIAL_LOCALE_NAMES
} from '@/lib/partner-material-text';
import { ContentEditorDialog } from '@/pages/admin/partner-content/components/ContentEditorDialog';
import {
  LocaleNav,
  type LocaleCompleteness
} from '@/pages/admin/partner-content/components/LocaleNav';
import { MaterialFileSlot } from './MaterialFileSlot';
import {
  MATERIAL_KIND_KEYS,
  MaterialFormSchema,
  NO_PENDING_FILES,
  canPublish,
  languagesAfterSave,
  toMaterialForm,
  type MaterialForm,
  type PendingFiles
} from './materialForm';
import { useSaveMaterial } from './useSaveMaterial';

const KIND_ICONS = { file: FileText, video: Video } as const;

function completenessOfParts(parts: boolean[]): LocaleCompleteness {
  if (parts.every(Boolean)) return 'complete';
  return parts.some(Boolean) ? 'partial' : 'empty';
}

function completenessOf(
  values: Pick<MaterialForm, 'kind' | 'title'>,
  languagesWithFile: PartnerMaterialLocale[]
): Record<PartnerMaterialLocale, LocaleCompleteness> {
  const entries = PARTNER_MATERIAL_LOCALES.map((locale) => {
    const parts = [Boolean(values.title[locale]?.trim())];
    if (values.kind === 'file') parts.push(languagesWithFile.includes(locale));
    return [locale, completenessOfParts(parts)] as const;
  });
  return Object.fromEntries(entries) as Record<PartnerMaterialLocale, LocaleCompleteness>;
}

interface MaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The material being edited, or null to create one. */
  material: PartnerMaterialAdmin | null;
}

/**
 * Mount with a fresh `key` per opening: form values and pending file changes
 * live here and must not carry over from one material to the next.
 */
export function MaterialDialog({ open, onOpenChange, material }: MaterialDialogProps) {
  const { t } = useTranslation();
  const { save, isSaving } = useSaveMaterial();
  const [locale, setLocale] = useState<PartnerMaterialLocale>('de');
  const [pending, setPending] = useState<PendingFiles>(NO_PENDING_FILES);

  const form = useForm<MaterialForm>({
    resolver: zodResolver(MaterialFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: toMaterialForm(material)
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const title = useWatch({ control: form.control, name: 'title' });
  const videoUrl = useWatch({ control: form.control, name: 'videoUrl' });

  const completeness = completenessOf({ kind, title }, languagesAfterSave(material, pending));
  const publishBlockedReason = canPublish(kind, material, pending)
    ? undefined
    : t('partnerContent.materials.needsFile');
  const previewVideoId = kind === 'video' ? parseYouTubeVideoId(videoUrl) : null;

  const pickFile = (target: PartnerMaterialLocale, file: File) =>
    setPending((current) => ({
      uploads: { ...current.uploads, [target]: file },
      removals: current.removals.filter((removed) => removed !== target)
    }));
  const removeFile = (target: PartnerMaterialLocale) =>
    setPending((current) => ({ ...current, removals: [...current.removals, target] }));
  const undoFile = (target: PartnerMaterialLocale) =>
    setPending((current) => {
      const { [target]: _undone, ...uploads } = current.uploads;
      return { uploads, removals: current.removals.filter((removed) => removed !== target) };
    });

  const submit = (publish: boolean) =>
    form.handleSubmit(
      async (values) => {
        try {
          await save({ existing: material, values, pending, publish });
          toast.success(
            t(publish ? 'partnerContent.toast.published' : 'partnerContent.toast.saved')
          );
          onOpenChange(false);
        } catch {
          toast.error(t('partnerContent.toast.saveFailed'));
        }
      },
      // German is the one required text, so a failed check shows its language.
      () => setLocale('de')
    )();

  const KindIcon = KIND_ICONS[kind];

  return (
    <ContentEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<KindIcon className="h-5 w-5" />}
      title={t(
        material ? 'partnerContent.materials.editTitle' : 'partnerContent.materials.createTitle'
      )}
      subtitle={t('partnerContent.materials.dialogDescription')}
      published={material ? material.active : null}
      isSaving={isSaving}
      publishBlockedReason={publishBlockedReason}
      onSave={submit}
    >
      <form noValidate onSubmit={(event) => event.preventDefault()} className="flex flex-col gap-6">
        {!material && (
          <DialogShellSection title={t('partnerContent.materials.kind')}>
            <div className="grid grid-cols-2 gap-2">
              {(['file', 'video'] as const).map((value) => {
                const Icon = KIND_ICONS[value];
                return (
                  <ChoiceCard
                    key={value}
                    icon={<Icon className="h-4 w-4" />}
                    title={t(MATERIAL_KIND_KEYS[value].label)}
                    hint={t(MATERIAL_KIND_KEYS[value].hint)}
                    selected={kind === value}
                    onClick={() => form.setValue('kind', value)}
                  />
                );
              })}
            </div>
          </DialogShellSection>
        )}

        <DialogShellSection title={t('partnerContent.materials.general')}>
          <FormSelect
            control={form.control}
            name="category"
            label={t('partner.infoHub.category')}
            options={PARTNER_MATERIAL_CATEGORIES.map((value) => ({
              value,
              label: t(PARTNER_MATERIAL_CATEGORY_KEYS[value])
            }))}
            required
          />
          {kind === 'video' && (
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <FormInput
                  control={form.control}
                  name="videoUrl"
                  type="url"
                  label={t('partnerContent.materials.videoUrl')}
                  required
                />
              </div>
              {previewVideoId && (
                <img
                  src={`https://i.ytimg.com/vi/${previewVideoId}/hqdefault.jpg`}
                  alt={t('partnerContent.materials.videoPreview')}
                  className="aspect-video w-40 rounded-md border object-cover"
                />
              )}
            </div>
          )}
        </DialogShellSection>

        <DialogShellSection
          title={t('partnerContent.languages.contentTitle')}
          hint={t('partnerContent.languages.hint')}
        >
          <DialogShellLayout
            sidebarWidth={180}
            sidebar={<LocaleNav active={locale} onSelect={setLocale} completeness={completeness} />}
          >
            {/* Keyed so each language mounts its own fields, not a re-bound copy. */}
            <div key={locale} className="flex flex-col gap-4">
              <FormInput
                control={form.control}
                name={`title.${locale}`}
                label={t('partnerContent.fields.titleIn', {
                  language: PARTNER_MATERIAL_LOCALE_NAMES[locale]
                })}
                required={locale === 'de'}
              />
              <FormTextarea
                control={form.control}
                name={`description.${locale}`}
                label={t('partnerContent.fields.descriptionIn', {
                  language: PARTNER_MATERIAL_LOCALE_NAMES[locale]
                })}
              />
              {kind === 'file' && (
                <div className="flex flex-col gap-2">
                  <Label>{t('partnerContent.materials.files.label')}</Label>
                  <MaterialFileSlot
                    locale={locale}
                    stored={material?.files[locale]}
                    pendingUpload={pending.uploads[locale]}
                    pendingRemoval={pending.removals.includes(locale)}
                    onPick={(file) => pickFile(locale, file)}
                    onRemove={() => removeFile(locale)}
                    onUndo={() => undoFile(locale)}
                  />
                </div>
              )}
            </div>
          </DialogShellLayout>
        </DialogShellSection>
      </form>
    </ContentEditorDialog>
  );
}
